// src/components/buyer-sales-list/order-table.tsx
import { Box, Checkbox, Group, Pagination, Table, Text } from "@mantine/core"
import { useTranslation } from "react-i18next"
import { IOrderPlus } from "../../models/Order"
import { TableFrame } from "../delivery/atoms"
import { HelpColumnTitle } from "../ui"
import { OrderStatusPill } from "../buyer-sales/order-status-pill"
import { currencyFormat } from "../../utils/currency"
import { dateFormat, dateTimeSecFormat } from "../../utils/date"
import { OrderRowActions } from "./order-row-actions"
import classes from "./order-table.module.css"

export type OrderTableColumns = { term: boolean; invoice: boolean }

interface OrderTableProps {
  orders: IOrderPlus[]
  columns: OrderTableColumns
  page: number
  limit: number
  total: number
  /** Client-side search over a wider window: no pagination, the range reads «N / total». */
  paginate: boolean
  onPageChange: (page: number) => void
  onRowAction: (action: string, order: IOrderPlus) => void
  permissions: {
    canDetail: boolean
    canEdit: boolean
    canCancel: boolean
    canReorder: boolean
  }
  currentUserId?: string
  reorderingId?: string | null
  /** Row multi-select for the bulk bar. */
  select: {
    isSelectable: (order: IOrderPlus) => boolean
    selectedIds: Set<string>
    onToggle: (id: string) => void
    onToggleAll: () => void
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Translate = (key: string, options?: any) => string

/**
 * Number cell (canvas OrdB_Orders): the supplier's SO number on top, the buyer's own PO
 * below. No SO yet (a draft or an order the supplier has not taken) → the PO on top
 * with «SO дугаар олгогдоогүй»; no PO (the supplier created it) → «Нийлүүлэгч үүсгэсэн».
 */
function orderNumber(order: IOrderPlus, t: Translate) {
  if (!order.salesCode) {
    return {
      primary: order.purchaseCode || t("order:orderStatus.DRAFT"),
      secondary: t("order:buyerOrders.noSalesCode"),
    }
  }
  return {
    primary: order.salesCode,
    secondary: order.purchaseCode
      ? t("order:buyerOrders.yourPo", { code: order.purchaseCode })
      : t("order:buyerOrders.supplierCreated"),
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function invoiceStatusText(invoice: any, t: Translate): string {
  if (!invoice?.refCode) return "-"
  const inv = invoice.invoiceStatus ? t(`order:buyerOrders.invoiceStatus.${invoice.invoiceStatus}`, { defaultValue: invoice.invoiceStatus }) : ""
  const pay = invoice.paymentStatus ? t(`order:buyerOrders.paymentStatus.${invoice.paymentStatus}`, { defaultValue: invoice.paymentStatus }) : ""
  return [inv, pay].filter(Boolean).join(", ") || "-"
}

export function OrderTable({
  orders,
  columns,
  page,
  limit,
  total,
  paginate,
  onPageChange,
  onRowAction,
  permissions,
  currentUserId,
  reorderingId,
  select,
}: OrderTableProps) {
  const { t } = useTranslation(["order", "common", "supplierSales"])
  const totalPages = Math.max(1, Math.ceil(total / limit))
  const from = paginate ? (page - 1) * limit + 1 : 1
  const to = paginate ? (page - 1) * limit + orders.length : orders.length
  const range = orders.length ? t("order:buyerOrders.range", { from, to, total }) : "0 / 0"
  const selectable = orders.filter((o) => o.id && select.isSelectable(o))
  const selectedCount = selectable.filter((o) => select.selectedIds.has(o.id!)).length
  const allSelected = selectable.length > 0 && selectedCount === selectable.length
  const paymentSpan = columns.term ? 2 : 1

  const footer = (
    <Group justify="space-between" wrap="wrap" gap="sm">
      <Text className={classes.meta}>{range}</Text>
      {paginate && totalPages > 1 && (
        <Pagination value={page} onChange={onPageChange} total={totalPages} color="dark" radius="md" />
      )}
    </Group>
  )

  return (
    <TableFrame footer={footer}>
      <Box className={classes.scroll}>
        <Table className={classes.table} verticalSpacing={0} horizontalSpacing={0}>
          <Table.Thead>
            <Table.Tr className={classes.groupRow}>
              <Table.Th className={classes.groupBlank} />
              <Table.Th className={classes.groupBlank} />
              <Table.Th colSpan={3}>{t("order:columns.order")}</Table.Th>
              <Table.Th>{t("order:columns.delivery")}</Table.Th>
              <Table.Th colSpan={paymentSpan}>{t("order:columns.payment")}</Table.Th>
              {columns.invoice && <Table.Th>{t("order:columns.invoice")}</Table.Th>}
            </Table.Tr>
            <Table.Tr className={classes.headRow}>
              <Table.Th w={44}>
                <Checkbox
                  aria-label={t("supplierSales:sales.list.selectAll")}
                  checked={allSelected}
                  indeterminate={selectedCount > 0 && !allSelected}
                  disabled={selectable.length === 0}
                  onChange={select.onToggleAll}
                />
              </Table.Th>
              <Table.Th><HelpColumnTitle helpKey="order.buyer-sales-list.field.col-actions">{t("common:table.actions")}</HelpColumnTitle></Table.Th>
              <Table.Th><HelpColumnTitle helpKey="order.buyer-sales-list.field.col-number">{t("order:buyerOrders.number")}</HelpColumnTitle></Table.Th>
              <Table.Th>{t("order:columns.date")}</Table.Th>
              <Table.Th><HelpColumnTitle helpKey="order.buyer-sales-list.field.col-status">{t("order:columns.status")}</HelpColumnTitle></Table.Th>
              <Table.Th><HelpColumnTitle helpKey="order.buyer-sales-list.field.col-delivery-day">{t("order:buyerOrders.deliveryDay")}</HelpColumnTitle></Table.Th>
              <Table.Th className={classes.right}>{t("order:columns.totalAmount")}</Table.Th>
              {columns.term && <Table.Th><HelpColumnTitle helpKey="order.buyer-sales-list.field.col-term">{t("order:buyerSales.paymentTerm")}</HelpColumnTitle></Table.Th>}
              {columns.invoice && <Table.Th><HelpColumnTitle helpKey="order.buyer-sales-list.field.col-invoice">{t("order:buyerOrders.invoiceNumberStatus")}</HelpColumnTitle></Table.Th>}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {orders.map((order) => {
              const number = orderNumber(order, t)
              return (
                <Table.Tr key={order.id || ""} className={classes.row} onClick={() => onRowAction("detail", order)}>
                  <Table.Td onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      aria-label={t("supplierSales:sales.list.selectRow")}
                      checked={!!order.id && select.selectedIds.has(order.id)}
                      disabled={!order.id || !select.isSelectable(order)}
                      onChange={() => order.id && select.onToggle(order.id)}
                    />
                  </Table.Td>
                  <Table.Td onClick={(e) => e.stopPropagation()}>
                    <OrderRowActions
                      order={order}
                      onDetail={() => onRowAction("detail", order)}
                      onReorder={() => onRowAction("reorder", order)}
                      onEdit={() => onRowAction("edit", order)}
                      onReview={() => onRowAction("review", order)}
                      onApprove={() => onRowAction("approve", order)}
                      onReject={() => onRowAction("reject", order)}
                      onPayment={() => onRowAction("payment", order)}
                      onCancel={() => onRowAction("cancel", order)}
                      permissions={permissions}
                      currentUserId={currentUserId}
                      reordering={reorderingId === order.id}
                    />
                  </Table.Td>
                  <Table.Td>
                    <Text className={classes.mono} translate="no">{number.primary}</Text>
                    <Text className={classes.sub}>{number.secondary}</Text>
                  </Table.Td>
                  <Table.Td className={classes.mono}>{order.createdAt ? dateTimeSecFormat(order.createdAt) : "-"}</Table.Td>
                  <Table.Td>
                    {/* The pill opens the status popover; only the pill itself stops the row's open-detail click. */}
                    <Box component="span" display="inline-block" onClick={(e) => e.stopPropagation()}>
                      <OrderStatusPill order={order} help />
                    </Box>
                  </Table.Td>
                  <Table.Td className={classes.mono}>{order.deliveryDate ? dateFormat(order.deliveryDate) : "-"}</Table.Td>
                  <Table.Td className={`${classes.mono} ${classes.right}`}>{currencyFormat(Number(order.totalAmount) || 0)}</Table.Td>
                  {columns.term && (
                    <Table.Td>
                      <Text className={classes.term} title={order.paymentTerm?.description || undefined}>
                        {order.paymentTerm?.description || "-"}
                      </Text>
                    </Table.Td>
                  )}
                  {columns.invoice && (
                    <Table.Td>
                      <Text className={classes.mono} translate="no">{order.invoice?.refCode || "-"}</Text>
                      <Text className={classes.sub}>{invoiceStatusText(order.invoice, t)}</Text>
                    </Table.Td>
                  )}
                </Table.Tr>
              )
            })}
          </Table.Tbody>
        </Table>
      </Box>
    </TableFrame>
  )
}
